import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { calcDocument, D, formatMoney, parseMoney } from '@makila/fiscal';
import {
  addItem, orderTotal, billableItems, bottleEmpty, itemUnitPrice, cancelItem, emitSale, findTabs, recordCashMovement, groupsFor, kitchenTicket, pickModifiers, mergeInto, moveItems, moveOrder, newOrder, newTab, orderLines, saveOrder, sendToKitchen, setItemQuantity, setItemStatus, splitEqual, tabState,
  type CatalogItem, type Customer, type LicenceStatus, type ModifierGroup, type Order, type OrderItem, type OrderModifier, type Signer,
} from '@makila/pos-core';
import { AlertTriangle, ArrowLeftRight, Beer, Bike, Check, ChefHat, CreditCard, GlassWater, LayoutDashboard, LayoutGrid, Minus, Moon, Plug, Plus, Printer, RefreshCw, Search, ShoppingBag, ShoppingCart, StickyNote, Sun, Ticket, Undo2, User, Users, Wallet, X } from 'lucide-react';
import clsx from 'clsx';
import { CustomerDialog } from '../components/CustomerDialog';
import { HardwareDialog } from '../components/HardwareDialog';
import { PanelItem, Rail, RailItem, RailTool, SidePanel } from '../components/Rail';
import { ManagerApproval } from '../components/ManagerApproval';
import { PayDialog } from '../components/PayDialog';
import { StatusPill } from '../components/StatusPill';
import { type DeviceState, hasModule, identity } from '../lib/device';
import type { IdbTerminalStore } from '../lib/idb-store';
import { kitchenTicketHtml, preBillHtml } from '../lib/kitchen';
import { receiptHtml } from '../lib/receipt';
import { printThermal } from '../lib/hw/printer';
import type { SyncStatus } from '../lib/sync';
import { useMedia } from '../lib/media';
import { useTheme } from '../lib/theme';
import type { Operator } from './Lock';
import type { Product } from './Sale';

interface Area { id: string; storeId: string; name: string; sortOrder: number; active: boolean }
interface Table { id: string; areaId: string; name: string; seats: number | null; sortOrder: number; active: boolean }
interface Category { id: string; name: string; color: string | null; sortOrder: number; active: boolean; station: string | null }

const data = <T,>(i: CatalogItem) => i.data as unknown as T;
const minutes = (iso: string) => Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
const total = orderTotal;
const STATUS: Record<OrderItem['status'], string> = { new: 'por enviar', sent: 'na cozinha', ready: 'pronto', served: 'servido', cancelled: 'anulado' };

interface Props {
  store: IdbTerminalStore; device: DeviceState; signer: Signer; licence: LicenceStatus | null; sync: SyncStatus | null;
  operator: Operator; cashSessionId: string;
  /** Balcão; com `open`, abre lá o painel da caixa ou a devolução. */
  onCounter: (open?: 'cash' | 'return') => void; onLock: () => void; onSold: () => void; onSyncNow: () => void;
  /** Gerente: abre (ou traz para a frente) a gestão. */
  onGestao?: () => void;
  /** Este equipamento passa a ser o ecrã da cozinha (com um gerente). */
  onKitchen?: () => void;
  /** Bar: o ecrã inicial são as contas por nome (não o mapa de mesas). */
  tabsHome?: boolean;
  /** Bar com entradas: o ecrã da porta. */
  onDoor?: () => void;
}

/** Restaurante/bar: mapa de mesas, contas por nome, take-away e a comanda de cada um. */
export function TablesScreen(p: Props) {
  const hasTabs = hasModule(p.device, 'tabs') && !!p.device.company.modules;
  const hasTables = hasModule(p.device, 'tables');
  const hasDoses = hasModule(p.device, 'doses') && !!p.device.company.modules;
  const [section, setSection] = useState<'tables' | 'tabs'>(hasTabs && (p.tabsHome || !hasTables) ? 'tabs' : 'tables');
  const [staff, setStaff] = useState<Map<string, string>>(new Map());
  const [newTabOpen, setNewTabOpen] = useState<string | null>(null);
  const [emptyOpen, setEmptyOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [areas, setAreas] = useState<Area[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [groups, setGroups] = useState<ModifierGroup[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [area, setArea] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toKitchen, setToKitchen] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [hwOpen, setHwOpen] = useState(false);
  const [dark, toggleTheme] = useTheme();
  const wide = useMedia('(min-width: 1024px)');

  const reload = useCallback(async () => {
    const store = p.device.store.id;
    const as = (await p.store.catalog('area')).map(data<Area>).filter((a) => a.active && a.storeId === store).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'pt'));
    setAreas(as);
    setTables((await p.store.catalog('table')).map(data<Table>).filter((t) => t.active && as.some((a) => a.id === t.areaId)).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'pt', { numeric: true })));
    setProducts((await p.store.catalog('product')).map(data<Product>).filter((x) => x.active && !x.soldByWeight && x.posVisible !== false && !x.openPrice && x.kind !== 'container').sort((a, b) => a.name.localeCompare(b.name, 'pt')));
    setCategories((await p.store.catalog('category')).map(data<Category>).filter((x) => x.active).sort((a, b) => a.sortOrder - b.sortOrder));
    setGroups((await p.store.catalog('modifier_group')).map(data<ModifierGroup & { sortOrder: number }>).filter((g) => g.active).sort((a, b) => a.sortOrder - b.sortOrder));
    setOrders(await p.store.orders());
    setStaff(new Map((await p.store.catalog('operator')).map((i) => [i.id, (i.data as { name: string }).name])));
  }, [p.store, p.device.store.id]);
  useEffect(() => { void reload(); }, [reload, p.sync?.lastSyncAt]);
  useEffect(() => { if (!area && areas[0]) setArea(areas[0].id); }, [areas, area]);

  const who = identity(p.device);
  const save = async (o: Order) => {
    try { const r = await saveOrder(p.store, who, o); p.onSold(); await reload(); return r; } catch (e) { setError((e as Error).message); return null; }
  };
  const open = orders.filter((o) => o.status === 'open');
  const tabs = open.filter((o) => o.kind === 'tab');
  const byTable = new Map(open.filter((o) => o.tableId).map((o) => [o.tableId!, o]));
  const current = openId ? orders.find((o) => o.id === openId) ?? null : null;

  const start = async (t: Table | null, delivery?: Order['delivery']) => {
    const takeaways = open.filter((o) => o.kind === 'takeaway').length;
    const o = newOrder({
      id: crypto.randomUUID(), kind: t ? 'table' : delivery ? 'delivery' : 'takeaway', tableId: t?.id ?? null,
      label: t ? `Mesa ${t.name}` : delivery ? `Entrega — ${delivery.name.trim()}` : `Take-away ${takeaways + 1}`,
      guests: null, operatorId: p.operator.id, terminalId: who.terminalId, now: new Date(), delivery,
    });
    const r = await save(o);
    if (r) setOpenId(r.id);
  };
  const startTab = async (f: { name: string; card: string; limit: string }) => {
    const limit = f.limit.trim() ? parseMoney(f.limit) : null;
    if (f.limit.trim() && !limit) throw new Error('Limite inválido');
    const o = newTab({ id: crypto.randomUUID(), name: f.name, card: f.card, limit, open, operatorId: p.operator.id, terminalId: who.terminalId, now: new Date() });
    const r = await save(o);
    if (r) { setNewTabOpen(null); setOpenId(r.id); }
  };

  // Os mesmos atalhos da barra principal que no balcão.
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (document.querySelector('dialog[open]')) return;
      if (e.key === 'F8' && p.licence?.sell) { e.preventDefault(); p.onCounter('return'); }
      if (e.key === 'F9') { e.preventDefault(); p.onCounter('cash'); }
      if (e.key === 'F10' && p.onGestao) { e.preventDefault(); p.onGestao(); }
    };
    addEventListener('keydown', on);
    return () => removeEventListener('keydown', on);
  });

  // A mesma barra principal da caixa: mesas, balcão, devolução e caixa; utilidades em baixo.
  const rail = (
    <Rail label="Caixa" footer={<>
      {p.onGestao && <RailItem icon={LayoutDashboard} label="Gestão" kbd="F10" ariaLabel="Abrir a gestão" onClick={p.onGestao} />}
      {p.onKitchen && <RailTool icon={ChefHat} label="Usar como ecrã da cozinha" onClick={() => setToKitchen(true)} />}
      <RailTool icon={Plug} label="Equipamento" onClick={() => setHwOpen(true)} />
      <RailTool icon={dark ? Sun : Moon} label={dark ? 'Modo claro' : 'Modo escuro'} onClick={toggleTheme} />
      <RailTool icon={RefreshCw} label="Sincronizar agora" onClick={p.onSyncNow} />
      <RailTool label="Trocar de operador" onClick={p.onLock}>
        <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-700 text-sm font-bold text-white">{p.operator.name.slice(0, 1).toUpperCase()}</span>
      </RailTool>
    </>}>
      {hasTabs && <RailItem icon={Beer} label="Contas" active={!current && section === 'tabs'} onClick={() => { setOpenId(null); setSection('tabs'); }} badge={tabs.length || null} />}
      {(hasTables || !hasTabs) && <RailItem icon={LayoutGrid} label="Mesas" active={!current && section === 'tables'} onClick={() => { setOpenId(null); setSection('tables'); }} badge={open.length - tabs.length || null} />}
      {p.onDoor && <RailItem icon={Ticket} label="Porta" onClick={p.onDoor} />}
      <RailItem icon={ShoppingCart} label="Balcão" onClick={() => p.onCounter()} />
      <RailItem icon={Undo2} label="Devolução" kbd="F8" disabled={!p.licence?.sell} onClick={() => p.onCounter('return')} />
      <RailItem icon={Wallet} label="Caixa" kbd="F9" onClick={() => p.onCounter('cash')} />
    </Rail>
  );
  const shell = (body: React.ReactNode) => (
    <div className="flex h-full flex-col-reverse md:flex-row">
      {rail}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">{body}</div>
      {hwOpen && <HardwareDialog onClose={() => setHwOpen(false)} />}
      {emptyOpen && <EmptyBottleDialog products={products} onClose={() => setEmptyOpen(false)} onEmpty={async (b) => {
        try {
          await bottleEmpty(p.store, who, { productId: b.id, operatorId: p.operator.id });
          setEmptyOpen(false); setNotice(`Garrafa de ${b.name} fechada. A próxima dose abre uma nova.`); p.onSold();
        } catch (e) { setError((e as Error).message); }
      }} />}
    </div>
  );

  if (current && current.status === 'open') {
    return shell(
      <OrderView {...p} staff={staff} order={current} products={products} categories={categories} groups={groups} tables={tables} openOrders={open}
        save={save} refresh={reload} onBack={() => { setOpenId(null); void reload(); }} onError={setError} error={error} />,
    );
  }

  const emptyBtn = hasDoses && <button onClick={() => setEmptyOpen(true)} className="flex h-12 items-center gap-2 rounded-xl border border-base px-3 font-medium hover:bg-surface-2"><GlassWater size={18} /> Garrafa vazia</button>;
  const banner = <>
    {error && <div role="alert" className="flex items-center gap-2 bg-amber-100 px-4 py-2 text-amber-900"><span className="flex-1">{error}</span><button aria-label="Fechar aviso" onClick={() => setError(null)}><X size={18} /></button></div>}
    {notice && <div role="status" className="flex items-center gap-2 bg-green-100 px-4 py-2 text-green-900"><span className="flex-1">{notice}</span><button aria-label="Fechar aviso" onClick={() => setNotice(null)}><X size={18} /></button></div>}
  </>;
  if (section === 'tabs') {
    return shell(
      <TabsView company={p.device.company.tradeName ?? p.device.company.legalName} subtitle={`${p.device.store.name} · ${p.device.terminal.name} · ${p.operator.name}`}
        sync={p.sync} tabs={tabs} staff={staff} banner={banner} extra={emptyBtn}
        onOpen={(o) => setOpenId(o.id)} onNew={(name) => setNewTabOpen(name)}>
        {newTabOpen !== null && <NewTabDialog initial={newTabOpen} onClose={() => setNewTabOpen(null)} onStart={startTab} />}
      </TabsView>,
    );
  }

  const busy = (a: Area) => tables.filter((t) => t.areaId === a.id && byTable.has(t.id)).length;
  return shell(
    <>
      <header className="flex min-h-16 flex-wrap items-center gap-x-4 gap-y-2 border-b border-base bg-[var(--surface)] px-4 py-2">
        <div className="min-w-0">
          <div className="truncate font-display text-[17px] font-semibold leading-tight">{p.device.company.tradeName ?? p.device.company.legalName}</div>
          <div className="truncate text-xs text-muted">{p.device.store.name} · {p.device.terminal.name} · {p.operator.name}</div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <StatusPill sync={p.sync} />
          {emptyBtn}
          {hasModule(p.device, 'delivery') && <>
            <button onClick={() => void start(null)} className="flex h-12 items-center gap-2 rounded-xl border border-base px-3 font-medium hover:bg-surface-2"><ShoppingBag size={18} /> Take-away</button>
            <button onClick={() => setDelivering(true)} className="flex h-12 items-center gap-2 rounded-xl border border-base px-3 font-medium hover:bg-surface-2"><Bike size={18} /> Entrega</button>
          </>}
        </div>
      </header>
      {banner}
      <div className="flex min-h-0 flex-1">
      {wide && areas.length > 0 && (
        <SidePanel title="Salas" subtitle={`${byTable.size} de ${tables.length} mesas ocupadas`}>
          <div className="space-y-0.5">
            {areas.map((a) => <PanelItem key={a.id} active={area === a.id} onClick={() => setArea(a.id)} trailing={`${busy(a)}/${tables.filter((t) => t.areaId === a.id).length}`}>{a.name}</PanelItem>)}
          </div>
        </SidePanel>
      )}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {!wide && (
        <div className="flex gap-2 overflow-x-auto border-b border-base p-3">
          {areas.map((a) => <button key={a.id} onClick={() => setArea(a.id)} className={clsx('h-12 shrink-0 rounded-full border px-4 font-medium', area === a.id ? 'border-brand-700 bg-brand-700 text-white' : 'border-base')}>{a.name}</button>)}
        </div>
      )}
      <main className="flex-1 overflow-y-auto p-4">
        {!areas.length && <p className="p-6 text-center text-muted">Ainda não há salas nem mesas. Crie-as na gestão (Mesas); chegam aqui na sincronização seguinte. Entretanto, use o Take-away.</p>}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {tables.filter((t) => t.areaId === area).map((t) => {
            const o = byTable.get(t.id);
            const fresh = o?.items.filter((i) => i.status === 'new').length ?? 0;
            const ready = o?.items.filter((i) => i.status === 'ready' && !i.paidIn).length ?? 0;
            return (
              <button key={t.id} onClick={() => (o ? setOpenId(o.id) : void start(t))} aria-label={`Mesa ${t.name}${o ? ' ocupada' : ' livre'}`}
                className={clsx('flex min-h-28 flex-col justify-between rounded-2xl border-2 p-3 text-left', o ? 'border-brand-700 bg-brand-50 dark:bg-brand-950' : 'border-base bg-[var(--surface)]')}>
                <span className="flex items-center justify-between"><span className="text-2xl font-bold">{t.name}</span>{t.seats && <span className="flex items-center gap-1 text-xs text-muted"><Users size={14} />{t.seats}</span>}</span>
                {o ? (
                  <span className="text-sm">
                    <span className="num block font-semibold">{formatMoney(total(o))} MT</span>
                    <span className="text-muted">{minutes(o.openedAt)} min</span>
                    {fresh > 0 && <span className="ml-2 rounded bg-amber-400 px-1 text-xs font-bold text-black">{fresh} por enviar</span>}
                    {ready > 0 && <span className="ml-2 rounded bg-green-600 px-1 text-xs font-bold text-white">{ready} pronto</span>}
                  </span>
                ) : <span className="text-sm text-muted">livre</span>}
              </button>
            );
          })}
        </div>
        {open.some((o) => o.kind !== 'table' && o.kind !== 'tab' && o.kind !== 'queue') && (
          <>
            <h2 className="mt-6 mb-2 font-semibold">Take-away e entregas</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              {open.filter((o) => o.kind !== 'table' && o.kind !== 'tab' && o.kind !== 'queue').map((o) => (
                <button key={o.id} onClick={() => setOpenId(o.id)} className="flex min-h-24 flex-col justify-between rounded-2xl border-2 border-brand-700 bg-brand-50 p-3 text-left dark:bg-brand-950">
                  <span className="font-bold">{o.label}</span>
                  {o.delivery && <span className="text-xs text-muted">{o.delivery.address}{o.delivery.phone ? ` · ${o.delivery.phone}` : ''}</span>}
                  <span className="num text-sm">{formatMoney(total(o))} MT · {minutes(o.openedAt)} min</span>
                </button>
              ))}
            </div>
          </>
        )}
      </main>
      </div>
      </div>
      {delivering && <DeliveryDialog onClose={() => setDelivering(false)} onStart={(d) => { setDelivering(false); void start(null, d); }} />}
      {toKitchen && (
        <Modal title="Ecrã da cozinha" onClose={() => setToKitchen(false)}>
          <p className="mb-3 text-sm text-muted">Este equipamento deixa de vender e passa a mostrar os pedidos à cozinha. Feche a caixa antes, se tiver vendas.</p>
          {p.operator.role === 'manager'
            ? <button onClick={() => { setToKitchen(false); p.onKitchen?.(); }} className="h-12 w-full rounded-xl bg-brand-700 font-bold text-white">Passar a ecrã da cozinha</button>
            : <ManagerApproval store={p.store} operator={p.operator} why="Um gerente autoriza a mudança." onApproved={() => { setToKitchen(false); p.onKitchen?.(); }} />}
        </Modal>
      )}
    </>,
  );
}

function OrderView(p: Props & {
  staff: Map<string, string>;
  order: Order; products: Product[]; categories: Category[]; groups: ModifierGroup[]; tables: Table[]; openOrders: Order[];
  save: (o: Order) => Promise<Order | null>; refresh: () => Promise<void>; onBack: () => void; onError: (e: string | null) => void; error: string | null;
}) {
  const o = p.order;
  const [q, setQ] = useState('');
  const [cat, setCat] = useState<string | null>(null);
  const [note, setNote] = useState<OrderItem | null>(null);
  const [cancel, setCancel] = useState<OrderItem | null>(null);
  const [move, setMove] = useState(false);
  const [bill, setBill] = useState(false);
  const [choosing, setChoosing] = useState<{ product: Product; groups: ModifierGroup[] } | null>(null);
  const station = useMemo(() => new Map(p.categories.map((c) => [c.id, c.station])), [p.categories]);
  // Toques seguidos: cada alteração parte da comanda já gravada pela anterior (uma fila), não da que estava no ecrã.
  const latest = useRef(o);
  useEffect(() => { latest.current = o; }, [o]);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const apply = (f: (x: Order) => Order) => {
    const run = queue.current.then(async () => { const r = await p.save(f(latest.current)); if (r) latest.current = r; return r; });
    queue.current = run.catch(() => null);
    return run;
  };
  const visible = p.products.filter((x) => (!cat || x.categoryId === cat) && (!q.trim() || x.name.toLowerCase().includes(q.trim().toLowerCase()) || x.barcode === q.trim()));
  const live = o.items.filter((i) => i.status !== 'cancelled' && !i.paidIn);
  const fresh = live.filter((i) => i.status === 'new');
  const lines = orderLines(o);
  const t = lines.length ? calcDocument({ lines }) : null;
  const isTab = o.kind === 'tab';
  const limit = isTab ? tabState(o) : null;
  // Sem cozinha (bar ao balcão): "Confirmar" marca o pedido como lançado, sem talão.
  const kitchen = hasModule(p.device, 'kitchen');

  /** Artigo com grupos de modificadores (ponto, extras, menu): primeiro a escolha. */
  const tap = (x: Product) => {
    const gs = groupsFor(p.groups, x.id, x.categoryId);
    if (gs.length) setChoosing({ product: x, groups: gs }); else add(x, []);
  };
  const add = (x: Product, modifiers: OrderModifier[]) => void apply((o) => {
    // Mais um igual (sem nota nem modificadores) soma à mesma linha; com modificadores, linha própria.
    const same = modifiers.length ? undefined : o.items.find((i) => i.productId === x.id && i.status === 'new' && !i.note && !i.modifiers.length && !i.paidIn);
    return same ? setItemQuantity(o, same.id, new D(same.quantity).plus(1).toString(), new Date())
      : addItem(o, { id: crypto.randomUUID(), productId: x.id, description: x.name, unitPrice: x.unitPrice, taxRate: x.taxRate, priceIncludesTax: x.priceIncludesTax, exemptionCode: x.exemptionCode, quantity: '1', modifiers, station: x.categoryId ? station.get(x.categoryId) ?? null : null, addedBy: p.operator.id }, new Date());
  });
  const send = async () => {
    let groups: ReturnType<typeof kitchenTicket> = [];
    let sent: Order | null = null;
    const r = await apply((x) => { groups = kitchenTicket(x).filter((g) => g.items.length); sent = x; return sendToKitchen(x, new Date()); });
    if (r && sent && groups.length && kitchen) void printThermal(kitchenTicketHtml(sent, groups, p.operator.name));
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex min-h-16 flex-wrap items-center gap-3 border-b border-base bg-[var(--surface)] px-4 py-2">
        <button onClick={p.onBack} className="flex h-12 items-center gap-2 rounded-xl px-3 font-semibold text-muted hover:bg-surface-2">← {isTab ? 'Contas' : 'Mesas'}</button>
        <div className="min-w-0">
          <div className="truncate font-display text-xl font-bold">{o.label}</div>
          {isTab && <div className="truncate text-xs text-muted">{[o.tab?.card && `Cartão ${o.tab.card}`, `aberta há ${minutes(o.openedAt)} min por ${p.staff.get(o.openedBy) ?? '—'}`, o.tab?.limit && `limite ${formatMoney(o.tab.limit)} MT`].filter(Boolean).join(' · ')}</div>}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <StatusPill sync={p.sync} />
          <button onClick={() => setMove(true)} className="flex h-12 items-center gap-2 rounded-xl border border-base px-3 font-medium hover:bg-surface-2"><ArrowLeftRight size={18} /> Mudar / juntar</button>
          <button onClick={() => void printThermal(preBillHtml(o, p.device.company.tradeName ?? p.device.company.legalName))} disabled={!live.length} className="flex h-12 items-center gap-2 rounded-xl border border-base px-3 font-medium hover:bg-surface-2 disabled:opacity-40"><Printer size={18} /> Consulta</button>
        </div>
      </header>
      {p.error && <div role="alert" className="flex items-center gap-2 bg-amber-100 px-4 py-2 text-amber-900"><span className="flex-1">{p.error}</span><button aria-label="Fechar aviso" onClick={() => p.onError(null)}><X size={18} /></button></div>}
      {limit && limit.level !== 'ok' && (
        <div role="alert" className={clsx('flex items-center gap-2 px-4 py-2 font-medium', limit.level === 'over' ? 'bg-brand-700 text-white' : 'bg-amber-100 text-amber-900')}>
          <AlertTriangle size={18} /> {limit.level === 'over' ? `Passou do limite de ${formatMoney(limit.limit!)} MT` : `Perto do limite de ${formatMoney(limit.limit!)} MT`}
        </div>
      )}
      <main className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[1fr_400px]">
        <section className="flex min-h-0 flex-col p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={20} />
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar" className="h-12 w-full rounded-xl border border-base bg-[var(--surface)] pl-11 pr-4" />
          </div>
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            <button onClick={() => setCat(null)} className={clsx('h-12 shrink-0 rounded-full border px-4 font-medium', !cat ? 'border-brand-700 bg-brand-700 text-white' : 'border-base')}>Todos</button>
            {p.categories.map((c) => <button key={c.id} onClick={() => setCat(c.id)} className={clsx('h-12 shrink-0 rounded-full border px-4 font-medium', cat === c.id ? 'border-brand-700 bg-brand-700 text-white' : 'border-base')}>{c.name}</button>)}
          </div>
          <div className="mt-3 grid min-h-0 flex-1 auto-rows-min grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3 xl:grid-cols-4">
            {visible.map((x) => (
              <button key={x.id} onClick={() => tap(x)} className="surface flex min-h-20 flex-col justify-between rounded-xl p-3 text-left hover:border-brand-500">
                <span className="line-clamp-2 font-medium">{x.name}</span><span className="num font-bold">{formatMoney(x.unitPrice)}</span>
              </button>
            ))}
          </div>
        </section>
        <aside className="flex min-h-0 flex-col border-t border-base bg-[var(--surface)] md:border-l md:border-t-0">
          {!isTab && <div className="flex items-center gap-2 border-b border-base px-3 py-2">
            <Users size={18} /><span className="flex-1">Pessoas: {o.guests ?? '—'}</span>
            <button aria-label="Menos pessoas" onClick={() => void apply((o) => ({ ...o, guests: Math.max(1, (o.guests ?? 1) - 1), updatedAt: new Date().toISOString() }))} className="grid h-12 w-12 place-items-center rounded-lg bg-surface-2"><Minus size={16} /></button>
            <button aria-label="Mais pessoas" onClick={() => void apply((o) => ({ ...o, guests: (o.guests ?? 0) + 1, updatedAt: new Date().toISOString() }))} className="grid h-12 w-12 place-items-center rounded-lg bg-surface-2"><Plus size={16} /></button>
          </div>}
          <ul className="flex-1 divide-y divide-[var(--border)] overflow-y-auto p-3">
            {!live.length && <li className="p-6 text-center text-muted">Toque nos artigos para os juntar à {isTab ? 'conta' : 'comanda'}.</li>}
            {live.map((i) => (
              <li key={i.id} className="py-2">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{i.quantity.replace('.', ',')} × {i.description}</div>
                    {i.modifiers.length > 0 && <div className="text-sm text-muted">{i.modifiers.map((m) => (new D(m.price).isZero() ? m.name : `${m.name} +${formatMoney(m.price)}`)).join(' · ')}</div>}
                    {i.note && <div className="text-sm italic text-muted">» {i.note}</div>}
                    <span className={clsx('mt-0.5 inline-block rounded px-1.5 text-xs font-semibold', { 'bg-amber-400 text-black': i.status === 'new', 'bg-surface-2': i.status === 'sent' || i.status === 'served', 'bg-green-600 text-white': i.status === 'ready' })}>{kitchen ? STATUS[i.status] : i.status === 'new' ? 'por confirmar' : 'lançado'}{kitchen && i.station && i.status !== 'new' ? ` · ${i.station}` : ''}</span>
                  </div>
                  <span className="num font-semibold">{formatMoney(new D(i.quantity).times(itemUnitPrice(i)).toDecimalPlaces(2))}</span>
                </div>
                <div className="mt-1 flex justify-end gap-2">
                  {i.status === 'new' && <>
                    <button aria-label={`Nota em ${i.description}`} onClick={() => setNote(i)} className="grid h-12 w-12 place-items-center rounded-lg bg-surface-2"><StickyNote size={16} /></button>
                    <button aria-label={`Menos ${i.description}`} onClick={() => void apply((o) => { const c = o.items.find((x) => x.id === i.id)!; return setItemQuantity(o, i.id, new D(c.quantity).minus(1).toString(), new Date()); })} className="grid h-12 w-12 place-items-center rounded-lg bg-surface-2"><Minus size={16} /></button>
                    <button aria-label={`Mais ${i.description}`} onClick={() => void apply((o) => { const c = o.items.find((x) => x.id === i.id)!; return setItemQuantity(o, i.id, new D(c.quantity).plus(1).toString(), new Date()); })} className="grid h-12 w-12 place-items-center rounded-lg bg-surface-2"><Plus size={16} /></button>
                  </>}
                  {i.status === 'ready' && <button onClick={() => void apply((o) => setItemStatus(o, i.id, 'served', new Date()))} className="h-10 rounded-lg bg-green-600 px-3 text-sm font-semibold text-white">Servido</button>}
                  {i.status !== 'new' && <button onClick={() => setCancel(i)} className="h-10 rounded-lg border border-base px-3 text-sm">Anular</button>}
                </div>
              </li>
            ))}
          </ul>
          <div className="border-t border-base p-3">
            <div className="flex items-baseline justify-between"><span className="text-lg">Total</span><span className="num text-3xl font-bold">{formatMoney(t?.grossTotal ?? '0')} MT</span></div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button disabled={!fresh.length} onClick={() => void send()} className="flex h-14 items-center justify-center gap-2 rounded-xl border-2 border-brand-700 font-bold text-brand-700 disabled:opacity-40">{kitchen ? <ChefHat size={20} /> : <Check size={20} />} {kitchen ? 'Enviar' : 'Confirmar'}{fresh.length ? ` (${fresh.length})` : ''}</button>
              <button disabled={!live.length || !p.licence?.sell} onClick={() => setBill(true)} className="h-14 rounded-xl bg-brand-700 text-xl font-bold text-white disabled:opacity-40">PAGAR</button>
            </div>
            {!live.length && o.items.every((i) => i.status === 'new' || i.status === 'cancelled') && (
              <button onClick={async () => { const r = await apply((o) => ({ ...o, status: 'cancelled', updatedAt: new Date().toISOString() })); if (r) p.onBack(); }} className="mt-2 h-12 w-full rounded-xl border border-base text-sm">Fechar {isTab ? 'conta' : 'mesa'} sem consumo</button>
            )}
          </div>
        </aside>
      </main>
      {choosing && <ModifierDialog product={choosing.product} groups={choosing.groups} onClose={() => setChoosing(null)} onPick={(m) => { add(choosing.product, m); setChoosing(null); }} />}
      {note && <NoteDialog item={note} onClose={() => setNote(null)} onSave={(text) => { const id = note.id; void apply((o) => ({ ...o, items: o.items.map((x) => (x.id === id ? { ...x, note: text || null, updatedAt: new Date().toISOString() } : x)), updatedAt: new Date().toISOString() })); setNote(null); }} />}
      {cancel && <CancelDialog item={cancel} store={p.store} operator={p.operator} onClose={() => setCancel(null)} onCancel={(reason, by) => { const id = cancel.id; void apply((o) => cancelItem(o, id, { reason, by }, new Date())); setCancel(null); }} />}
      {move && isTab && <TabMoveDialog order={o} openOrders={p.openOrders} onClose={() => setMove(false)} onMove={async (target, itemIds) => {
        await queue.current;
        try {
          if (itemIds) { const r = moveItems(latest.current, target, itemIds, new Date()); await p.save(r.target); const s = await p.save(r.source); if (s) latest.current = s; setMove(false); }
          else { const r = mergeInto(target, latest.current, new Date()); await p.save(r.target); await p.save(r.source); setMove(false); p.onBack(); }
        } catch (e) { p.onError((e as Error).message); }
      }} />}
      {move && !isTab && <MoveDialog order={o} tables={p.tables} openOrders={p.openOrders} onClose={() => setMove(false)} onMove={async (t) => {
        const other = p.openOrders.find((x) => x.tableId === t.id && x.id !== o.id);
        await queue.current;
        if (other) { const r = mergeInto(other, latest.current, new Date()); await p.save(r.target); await p.save(r.source); }
        else await apply((o) => moveOrder(o, t.id, `Mesa ${t.name}`, new Date()));
        setMove(false); p.onBack();
      }} />}
      {bill && t && p.licence && <BillDialog {...p} order={o} onClose={() => setBill(false)} onPaid={() => { setBill(false); void p.refresh(); }} />}
    </div>
  );
}

function Modal(p: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={p.title} className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-3">
      <div className={clsx('max-h-[90vh] w-full overflow-y-auto rounded-2xl bg-[var(--surface)]', p.wide ? 'max-w-2xl' : 'max-w-md')}>
        <div className="flex items-center justify-between border-b border-base px-5 py-3"><h2 className="text-xl font-bold">{p.title}</h2><button aria-label="Fechar" onClick={p.onClose} className="grid h-12 w-12 place-items-center"><X /></button></div>
        <div className="p-5">{p.children}</div>
      </div>
    </div>
  );
}

function NoteDialog(p: { item: OrderItem; onClose: () => void; onSave: (t: string) => void }) {
  const [v, setV] = useState(p.item.note ?? '');
  const quick = ['Sem cebola', 'Bem passado', 'Mal passado', 'Sem gelo', 'Para levar'];
  return (
    <Modal title={`Nota — ${p.item.description}`} onClose={p.onClose}>
      <div className="flex flex-wrap gap-2">{quick.map((x) => <button key={x} onClick={() => setV(v ? `${v}, ${x}` : x)} className="h-10 rounded-lg border border-base px-3 text-sm">{x}</button>)}</div>
      <input autoFocus aria-label="Nota para a cozinha" value={v} onChange={(e) => setV(e.target.value)} maxLength={120} className="mt-3 h-12 w-full rounded-xl border border-base bg-surface-2 px-4" />
      <button onClick={() => p.onSave(v.trim())} className="mt-3 h-12 w-full rounded-xl bg-brand-700 font-bold text-white">Gravar</button>
    </Modal>
  );
}

/** Anular um item já enviado: motivo e gerente (proposta §3: anulação de item enviado com motivo e permissão). */
function CancelDialog(p: { item: OrderItem; store: IdbTerminalStore; operator: Operator; onClose: () => void; onCancel: (reason: string, by: string) => void }) {
  const [approver, setApprover] = useState<Operator | null>(p.operator.role === 'manager' ? p.operator : null);
  const [reason, setReason] = useState('');
  return (
    <Modal title={`Anular ${p.item.description}`} onClose={p.onClose}>
      {!approver ? <ManagerApproval store={p.store} operator={p.operator} why="Anular um item que já foi para a cozinha precisa de um gerente." onApproved={setApprover} /> : (
        <>
          <input autoFocus aria-label="Motivo da anulação" placeholder="Motivo (obrigatório)" value={reason} onChange={(e) => setReason(e.target.value)} className="h-12 w-full rounded-xl border border-base bg-surface-2 px-4" />
          <button disabled={!reason.trim()} onClick={() => p.onCancel(reason.trim(), approver.id)} className="mt-3 h-12 w-full rounded-xl bg-brand-700 font-bold text-white disabled:opacity-40">Anular</button>
        </>
      )}
    </Modal>
  );
}

function MoveDialog(p: { order: Order; tables: Table[]; openOrders: Order[]; onClose: () => void; onMove: (t: Table) => void }) {
  return (
    <Modal title={`Mudar ${p.order.label}`} onClose={p.onClose} wide>
      <p className="mb-3 text-sm text-muted">Mesa livre: a comanda muda para lá. Mesa ocupada: as duas juntam-se numa só conta.</p>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        {p.tables.filter((t) => t.id !== p.order.tableId).map((t) => {
          const busy = p.openOrders.some((x) => x.tableId === t.id);
          return <button key={t.id} onClick={() => p.onMove(t)} className={clsx('h-16 rounded-xl border-2 font-bold', busy ? 'border-brand-700' : 'border-base')}>{t.name}<span className="block text-xs font-normal text-muted">{busy ? 'juntar' : 'livre'}</span></button>;
        })}
      </div>
    </Modal>
  );
}

/** Pagar: tudo, só alguns itens (cada um paga o seu), ou em partes iguais (uma conta, vários pagamentos). */
function BillDialog(p: Props & { order: Order; onClose: () => void; onPaid: () => void }) {
  const items = billableItems(p.order);
  const [mode, setMode] = useState<'all' | 'items' | 'equal'>('all');
  const [pick, setPick] = useState<string[]>([]);
  const [parts, setParts] = useState('2');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [pickCustomer, setPickCustomer] = useState(false);
  const [paying, setPaying] = useState(false);
  // Gorjeta: fora da factura; vai para o empregado da mesa (registo da caixa).
  const [tip, setTip] = useState('');
  const ids = mode === 'items' ? pick : undefined;
  const lines = orderLines(p.order, ids);
  const t = lines.length ? calcDocument({ lines }) : null;
  const split = mode === 'equal' && t && /^\d+$/.test(parts) && Number(parts) >= 2 ? (() => { try { return splitEqual(t.grossTotal, Number(parts)); } catch { return null; } })() : null;
  if (paying && t) {
    return (
      <PayDialog total={t.grossTotal} onClose={() => setPaying(false)} hint={split ? `Partes iguais: ${split.map((x) => formatMoney(x)).join(' + ')} — registe um pagamento por pessoa.` : undefined}
        onConfirm={async (tenders) => {
          const doc = await emitSale(p.store, identity(p.device), p.signer, {
            docType: customer ? 'FR' : 'VD', customer, lines, tenders, operatorId: p.operator.id, cashSessionId: p.cashSessionId, licence: p.licence!, order: { id: p.order.id, itemIds: ids },
          });
          const t = parseMoney(tip || '0');
          if (t && new D(t).greaterThan(0)) {
            await recordCashMovement(p.store, identity(p.device), { kind: 'tip', amount: t, reason: `Gorjeta — ${p.order.label}`, operatorId: p.operator.id, beneficiaryId: p.order.openedBy, method: 'NU' });
          }
          p.onSold();
          void printThermal(receiptHtml(doc, p.device), { drawer: doc.payments.some((x) => x.method === 'NU') });
          p.onPaid();
        }} />
    );
  }
  return (
    <Modal title={`Conta — ${p.order.label}`} onClose={p.onClose} wide>
      <div role="tablist" className="grid grid-cols-3 gap-2">
        {([['all', 'Tudo'], ['items', 'Por itens'], ['equal', 'Partes iguais']] as const).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={mode === k} onClick={() => setMode(k)} className={clsx('h-12 rounded-xl border font-semibold', mode === k ? 'border-brand-700 bg-brand-700 text-white' : 'border-base')}>{l}</button>
        ))}
      </div>
      {mode === 'items' && (
        <ul className="mt-3 divide-y divide-[var(--border)]">
          {items.map((i) => (
            <li key={i.id}><label className="flex h-12 items-center gap-3"><input type="checkbox" checked={pick.includes(i.id)} onChange={() => setPick(pick.includes(i.id) ? pick.filter((x) => x !== i.id) : [...pick, i.id])} className="h-5 w-5" />
              <span className="flex-1">{i.quantity.replace('.', ',')} × {i.description}</span><span className="num">{formatMoney(new D(i.quantity).times(itemUnitPrice(i)).toDecimalPlaces(2))}</span></label></li>
          ))}
        </ul>
      )}
      {mode === 'equal' && (
        <div className="mt-3 flex items-center gap-3">
          <span>Dividir por</span>
          <input aria-label="Número de partes" inputMode="numeric" value={parts} onChange={(e) => setParts(e.target.value.replace(/\D/g, ''))} className="num h-12 w-20 rounded-xl border border-base bg-surface-2 text-center text-xl" />
          <span className="num text-muted">{split ? split.map((x) => formatMoney(x)).join(' + ') : ''}</span>
        </div>
      )}
      <button onClick={() => setPickCustomer(true)} className="mt-4 flex h-12 w-full items-center gap-2 rounded-lg bg-surface-2 px-3 text-left">
        <User size={18} /> <span className="flex-1 truncate">{customer ? `${customer.name}${customer.nuit ? ` · ${customer.nuit}` : ''}` : 'Consumidor Final'}</span><span className="text-xs text-muted">{customer ? 'Factura-recibo' : 'Venda a dinheiro'}</span>
      </button>
      <label className="mt-3 flex items-center gap-3 text-sm">Gorjeta em numerário (fora da factura)
        <input aria-label="Gorjeta" inputMode="decimal" placeholder="0,00" value={tip} onChange={(e) => setTip(e.target.value)} className="num h-12 w-28 rounded-lg border border-base bg-surface-2 px-2 text-right" />
      </label>
      <div className="num mt-4 flex items-baseline justify-between text-2xl font-bold"><span>A pagar</span><span>{formatMoney(t?.grossTotal ?? '0')} MT</span></div>
      <button disabled={!t || (mode === 'equal' && !split)} onClick={() => setPaying(true)} className="mt-4 h-14 w-full rounded-xl bg-brand-700 text-xl font-bold text-white disabled:opacity-40">Pagar</button>
      {pickCustomer && <CustomerDialog store={p.store} who={identity(p.device)} onClose={() => setPickCustomer(false)} onPick={(c) => { setCustomer(c); setPickCustomer(false); }} />}
    </Modal>
  );
}

/** Escolha dos modificadores (ponto da carne, extras, o que leva o menu). */
function ModifierDialog(p: { product: Product; groups: ModifierGroup[]; onClose: () => void; onPick: (m: OrderModifier[]) => void }) {
  const [chosen, setChosen] = useState<Record<string, string[]>>({});
  const [err, setErr] = useState<string | null>(null);
  const toggle = (g: ModifierGroup, name: string) => {
    const cur = chosen[g.id] ?? [];
    const next = cur.includes(name) ? cur.filter((x) => x !== name) : g.max === 1 ? [name] : cur.length < g.max ? [...cur, name] : cur;
    setChosen({ ...chosen, [g.id]: next });
    setErr(null);
  };
  let extra = new D(0);
  for (const g of p.groups) for (const n of chosen[g.id] ?? []) extra = extra.plus(g.options.find((o) => o.name === n)?.price ?? 0);
  return (
    <Modal title={p.product.name} onClose={p.onClose} wide>
      {p.groups.map((g) => (
        <section key={g.id} className="mb-4">
          <h3 className="font-semibold">{g.name} <span className="text-sm font-normal text-muted">{g.min > 0 ? (g.max === 1 ? 'escolha uma' : `escolha ${g.min} a ${g.max}`) : `opcional${g.max > 1 ? `, até ${g.max}` : ''}`}</span></h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {g.options.map((o) => {
              const on = (chosen[g.id] ?? []).includes(o.name);
              return <button key={o.name} aria-pressed={on} onClick={() => toggle(g, o.name)} className={clsx('h-12 rounded-xl border px-4 font-medium', on ? 'border-brand-700 bg-brand-700 text-white' : 'border-base')}>{o.name}{!new D(o.price).isZero() && <span className="ml-1 text-sm opacity-80">+{formatMoney(o.price)}</span>}</button>;
            })}
          </div>
        </section>
      ))}
      {err && <p role="alert" className="mb-2 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{err}</p>}
      <button onClick={() => { try { p.onPick(pickModifiers(p.groups, chosen)); } catch (e) { setErr((e as Error).message); } }} className="h-14 w-full rounded-xl bg-brand-700 text-lg font-bold text-white">
        Juntar · {formatMoney(new D(p.product.unitPrice).plus(extra))} MT
      </button>
    </Modal>
  );
}

function DeliveryDialog(p: { onClose: () => void; onStart: (d: { name: string; phone: string | null; address: string }) => void }) {
  const [f, setF] = useState({ name: '', phone: '', address: '' });
  return (
    <Modal title="Entrega" onClose={p.onClose}>
      <form onSubmit={(e) => { e.preventDefault(); p.onStart({ name: f.name, phone: f.phone || null, address: f.address }); }} className="space-y-3">
        <input autoFocus required aria-label="Nome do cliente" placeholder="Nome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-12 w-full rounded-xl border border-base bg-surface-2 px-4" />
        <input aria-label="Telefone" inputMode="tel" placeholder="Telefone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="h-12 w-full rounded-xl border border-base bg-surface-2 px-4" />
        <input required aria-label="Morada da entrega" placeholder="Morada (bairro, rua, referência)" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className="h-12 w-full rounded-xl border border-base bg-surface-2 px-4" />
        <button className="h-12 w-full rounded-xl bg-brand-700 font-bold text-white">Abrir a comanda</button>
      </form>
    </Modal>
  );
}

/** Contas por nome (bar): procurar pelo nome ou pelo cartão, abrir, ver o que cada uma deve. */
function TabsView(p: {
  company: string; subtitle: string; sync: SyncStatus | null; tabs: Order[]; staff: Map<string, string>; banner: React.ReactNode; extra: React.ReactNode;
  onOpen: (o: Order) => void; onNew: (name: string) => void; children?: React.ReactNode;
}) {
  const [q, setQ] = useState('');
  const found = findTabs(p.tabs, q).sort((a, b) => a.label.localeCompare(b.label, 'pt'));
  const sum = p.tabs.reduce((s, o) => s.plus(tabState(o).total), new D(0));
  return (
    <>
      <header className="flex min-h-16 flex-wrap items-center gap-x-4 gap-y-2 border-b border-base bg-[var(--surface)] px-4 py-2">
        <div className="min-w-0">
          <div className="truncate font-display text-[17px] font-semibold leading-tight">Contas</div>
          <div className="truncate text-xs text-muted">{p.tabs.length === 1 ? '1 aberta' : `${p.tabs.length} abertas`} · {formatMoney(sum)} MT por cobrar · {p.subtitle}</div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <StatusPill sync={p.sync} />
          {p.extra}
          <button onClick={() => p.onNew(q.trim())} className="flex h-12 items-center gap-2 rounded-xl bg-brand-700 px-4 font-bold text-white hover:bg-brand-800"><Plus size={18} /> Nova conta</button>
        </div>
      </header>
      {p.banner}
      <main className="flex-1 overflow-y-auto p-4">
        <div className="relative max-w-xl">
          <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted" size={20} />
          <input autoFocus type="search" aria-label="Procurar conta: nome ou cartão" placeholder="Nome ou número do cartão" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key !== 'Enter' || !q.trim()) return; e.preventDefault(); if (found.length === 1) p.onOpen(found[0]!); else if (!found.length) p.onNew(q.trim()); }}
            className="h-14 w-full rounded-2xl border border-base bg-[var(--surface)] pl-12 pr-4 text-lg outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10" />
        </div>
        {!p.tabs.length && <p className="mt-10 text-center text-muted">Não há contas abertas. Toque em <b>Nova conta</b> e escreva o nome do cliente.</p>}
        {q.trim() && !found.length && p.tabs.length > 0 && (
          <button onClick={() => p.onNew(q.trim())} className="mt-4 flex h-14 items-center gap-2 rounded-xl border-2 border-dashed border-base px-4 font-medium hover:border-brand-500"><Plus size={18} /> Abrir conta «{q.trim()}»</button>
        )}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6">
          {found.map((o) => {
            const st = tabState(o);
            const pct = st.limit ? Math.min(100, Number(new D(st.total).div(st.limit).times(100).toFixed(0))) : null;
            const fresh = o.items.filter((i) => i.status === 'new').length;
            return (
              <button key={o.id} onClick={() => p.onOpen(o)} aria-label={`Conta ${o.label}`}
                className={clsx('flex min-h-32 flex-col justify-between rounded-2xl border-2 bg-[var(--surface)] p-3.5 text-left hover:border-brand-500', st.level === 'over' ? 'border-brand-700' : st.level === 'near' ? 'border-amber-500' : 'border-base')}>
                <span className="flex items-start justify-between gap-2">
                  <span className="line-clamp-2 text-lg font-bold leading-tight">{o.label}</span>
                  {o.tab?.card && <span className="flex shrink-0 items-center gap-1 rounded-md bg-surface-2 px-1.5 py-0.5 text-xs font-semibold"><CreditCard size={12} />{o.tab.card}</span>}
                </span>
                <span>
                  <span className="num block text-xl font-bold">{formatMoney(st.total)} MT</span>
                  <span className="block truncate text-xs text-muted">{minutes(o.openedAt)} min · {p.staff.get(o.openedBy) ?? '—'}{fresh > 0 ? ` · ${fresh} por confirmar` : ''}</span>
                  {pct !== null && (
                    <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-surface-2" title={`Limite ${formatMoney(st.limit!)} MT`}>
                      <span className={clsx('block h-full rounded-full', st.level === 'over' ? 'bg-brand-700' : st.level === 'near' ? 'bg-amber-500' : 'bg-green-600')} style={{ width: `${pct}%` }} />
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </main>
      {p.children}
    </>
  );
}

function NewTabDialog(p: { initial: string; onClose: () => void; onStart: (f: { name: string; card: string; limit: string }) => Promise<void> }) {
  const [f, setF] = useState({ name: p.initial, card: '', limit: '' });
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal title="Nova conta" onClose={p.onClose}>
      <form onSubmit={async (e) => { e.preventDefault(); setErr(null); try { await p.onStart(f); } catch (x) { setErr((x as Error).message); } }} className="space-y-3">
        <label className="block text-sm font-medium">Nome <span className="text-brand-700">*</span>
          <input autoFocus required aria-label="Nome da conta" placeholder="Ex.: João, Mesa do fundo" value={f.name} maxLength={60} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 h-12 w-full rounded-xl border border-base bg-surface-2 px-4 text-base font-normal" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm font-medium">Cartão ou pulseira
            <input aria-label="Número do cartão" inputMode="numeric" placeholder="opcional" value={f.card} maxLength={20} onChange={(e) => setF({ ...f, card: e.target.value })} className="num mt-1 h-12 w-full rounded-xl border border-base bg-surface-2 px-4 text-base font-normal" />
          </label>
          <label className="block text-sm font-medium">Limite de consumo
            <span className="mt-1 flex items-center gap-2"><input aria-label="Limite de consumo" inputMode="decimal" placeholder="sem limite" value={f.limit} onChange={(e) => setF({ ...f, limit: e.target.value })} className="num h-12 w-full rounded-xl border border-base bg-surface-2 px-4 text-right text-base font-normal" /><span className="text-sm text-muted">MT</span></span>
          </label>
        </div>
        {err && <p role="alert" className="rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{err}</p>}
        <button className="h-14 w-full rounded-xl bg-brand-700 text-lg font-bold text-white">Abrir a conta</button>
      </form>
    </Modal>
  );
}

/** Conta: juntar toda a outra conta, ou passar só alguns itens (o amigo que paga à parte). */
function TabMoveDialog(p: { order: Order; openOrders: Order[]; onClose: () => void; onMove: (target: Order, itemIds: string[] | null) => void }) {
  const [mode, setMode] = useState<'items' | 'all'>('items');
  const [pick, setPick] = useState<string[]>([]);
  const others = p.openOrders.filter((x) => x.kind === 'tab' && x.id !== p.order.id).sort((a, b) => a.label.localeCompare(b.label, 'pt'));
  const items = billableItems(p.order);
  return (
    <Modal title={`Mudar — ${p.order.label}`} onClose={p.onClose} wide>
      <div role="tablist" className="grid grid-cols-2 gap-2">
        {([['items', 'Passar itens'], ['all', 'Juntar a conta toda']] as const).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={mode === k} onClick={() => setMode(k)} className={clsx('h-12 rounded-xl border font-semibold', mode === k ? 'border-brand-700 bg-brand-700 text-white' : 'border-base')}>{l}</button>
        ))}
      </div>
      {mode === 'items' && (
        <ul className="mt-3 divide-y divide-[var(--border)]">
          {items.map((i) => (
            <li key={i.id}><label className="flex h-12 items-center gap-3"><input type="checkbox" checked={pick.includes(i.id)} onChange={() => setPick(pick.includes(i.id) ? pick.filter((x) => x !== i.id) : [...pick, i.id])} className="h-5 w-5" />
              <span className="flex-1">{i.quantity.replace('.', ',')} × {i.description}</span><span className="num">{formatMoney(new D(i.quantity).times(itemUnitPrice(i)).toDecimalPlaces(2))}</span></label></li>
          ))}
        </ul>
      )}
      <p className="mb-2 mt-4 text-sm text-muted">{mode === 'items' ? 'Para a conta:' : `Os itens de ${p.order.label} passam para:`}</p>
      {!others.length && <p className="text-sm text-muted">Não há outras contas abertas.</p>}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {others.map((t) => (
          <button key={t.id} disabled={mode === 'items' && !pick.length} onClick={() => p.onMove(t, mode === 'items' ? pick : null)} className="flex h-16 flex-col items-start justify-center rounded-xl border-2 border-base px-3 text-left hover:border-brand-500 disabled:opacity-40">
            <span className="w-full truncate font-bold">{t.label}</span><span className="num text-xs text-muted">{formatMoney(tabState(t).total)} MT</span>
          </button>
        ))}
      </div>
    </Modal>
  );
}

/** "Garrafa vazia": fecha a garrafa aberta; o que faltava vender fica como perda (relatório na gestão). */
function EmptyBottleDialog(p: { products: Product[]; onClose: () => void; onEmpty: (b: Product) => void }) {
  const [q, setQ] = useState('');
  const [sure, setSure] = useState<Product | null>(null);
  const bottles = p.products.filter((x) => (x as Product & { bottleMl?: string | null }).bottleMl && (!q.trim() || x.name.toLowerCase().includes(q.trim().toLowerCase())));
  return (
    <Modal title="Garrafa vazia" onClose={p.onClose} wide>
      {sure ? (
        <>
          <p>Fechar a garrafa aberta de <b>{sure.name}</b>? O que o sistema ainda esperava vender dela fica registado como perda.</p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button onClick={() => setSure(null)} className="h-14 rounded-xl border border-base font-semibold">Voltar</button>
            <button onClick={() => p.onEmpty(sure)} className="h-14 rounded-xl bg-brand-700 font-bold text-white">Fechar a garrafa</button>
          </div>
        </>
      ) : (
        <>
          <input autoFocus type="search" aria-label="Procurar garrafa" placeholder="Procurar" value={q} onChange={(e) => setQ(e.target.value)} className="h-12 w-full rounded-xl border border-base bg-surface-2 px-4" />
          {!bottles.length && <p className="mt-4 text-sm text-muted">Nenhuma garrafa com doses. Na gestão, preencha a capacidade (ml) da garrafa e crie a dose que sai dela.</p>}
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {bottles.map((b) => <button key={b.id} onClick={() => setSure(b)} className="flex h-16 items-center gap-2 rounded-xl border-2 border-base px-3 text-left font-semibold hover:border-brand-500"><GlassWater size={18} className="shrink-0 text-muted" /><span className="line-clamp-2">{b.name}</span></button>)}
          </div>
        </>
      )}
    </Modal>
  );
}
